import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { handleLogout, httpServices } from '@/api/client';
import { AppConstants } from '@/utils/constants';
import { currentTokenExpiry } from '@/utils/session';
import { useAnnounce } from './LiveRegion';
import { minutesLeft, planExpiry } from './sessionExpiry';

/** How often to look for a renewed token (Auth0 refreshes it silently). */
const RECHECK_MS = 30_000;

/**
 * Warns two minutes before the session token expires and offers to sign in
 * again; when it expires, signs out to the sign-in page, which says why.
 * Auth0 sessions first try a silent renewal and only warn if it fails.
 */
const SessionExpiryWarning = () => {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();
  const [expiresAt, setExpiresAt] = useState<number | null>(currentTokenExpiry);
  const [warning, setWarning] = useState(false);
  const [now, setNow] = useState(Date.now);
  const dismissedFor = useRef<number | null>(null);

  // The token can change (renewal, another tab); re-read its expiry.
  useEffect(() => {
    const id = setInterval(
      () => setExpiresAt(currentTokenExpiry()),
      RECHECK_MS,
    );
    return () => clearInterval(id);
  }, []);

  const renewSilently = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated) return false;
    try {
      const token = await getAccessTokenSilently({ cacheMode: 'off' });
      httpServices.llmService.updateToken(token);
      httpServices.saasService.updateToken(token);
      setExpiresAt(currentTokenExpiry());
      return true;
    } catch {
      return false;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    setWarning(false);
    const plan = planExpiry(expiresAt, Date.now());
    if (plan.warnIn == null || plan.expireIn == null) return undefined;
    const warnTimer = setTimeout(async () => {
      if (await renewSilently()) return;
      if (dismissedFor.current === expiresAt) return;
      setNow(Date.now());
      setWarning(true);
    }, plan.warnIn);
    const expireTimer = setTimeout(async () => {
      if (await renewSilently()) return;
      handleLogout('expired');
    }, plan.expireIn);
    return () => {
      clearTimeout(warnTimer);
      clearTimeout(expireTimer);
    };
  }, [expiresAt, renewSilently]);

  useEffect(() => {
    if (warning) announce(t('v2.shell.sessionExpiringTitle'), 'assertive');
  }, [warning, announce, t]);

  const reauthenticate = () => {
    setWarning(false);
    if (isAuthenticated) {
      void loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      });
      return;
    }
    // A SaaS session gets a new token from the SaaS frame.
    if (window.self !== window.top) {
      window.parent?.postMessage(
        { type: AppConstants.reloadMessage },
        AppConstants.SAAS_DOMAIN,
      );
      return;
    }
    handleLogout('expired');
  };

  if (!warning || expiresAt == null) return null;
  return (
    <Modal
      open
      title={t('v2.shell.sessionExpiringTitle')}
      onCancel={() => {
        dismissedFor.current = expiresAt;
        setWarning(false);
      }}
      footer={[
        <Button
          key="later"
          onClick={() => {
            dismissedFor.current = expiresAt;
            setWarning(false);
          }}
        >
          {t('v2.shell.later')}
        </Button>,
        <Button key="reauth" type="primary" onClick={reauthenticate} autoFocus>
          {t('v2.shell.reauth')}
        </Button>,
      ]}
    >
      <p>
        {t('v2.shell.sessionExpiring', {
          minutes: minutesLeft(expiresAt, now),
        })}
      </p>
    </Modal>
  );
};

export default SessionExpiryWarning;
